import React from 'react';
import dayjs from 'dayjs';
import { useRecoilValue } from 'recoil';
import { Box, ButtonGroup, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CloudDownload, Print } from '@mui/icons-material';
import { serviceOrdersAtom } from './serviceOrdersPage.atoms';
import useServiceOrdersColumns from './useServiceOrdersColumns';
import exportAsPDF from 'services/exportAsPDF';
import { CSVLink } from 'react-csv';
import useActionsParams from './useActionsParams';
import { SummaryServiceOrder } from 'types/serviceOrder.types';
import DateRangePicker, { Value, ValuePiece } from 'components/DateRangePicker';

function ServiceOrdersActions() {
  const { t } = useTranslation(['_common', 'service_order']);
  const serviceOrders = useRecoilValue(serviceOrdersAtom);
  const columns = useServiceOrdersColumns({ t });
  const { beginDate, endDate, setDates } = useActionsParams();

  function handlePrintClick() {
    serviceOrders &&
      exportAsPDF({
        rows: serviceOrders as SummaryServiceOrder[],
        t,
        title: t('_common:service_orders'),
        columns,
      });
  }

  const eventsCsvData = React.useMemo(() => {
    return (
      serviceOrders && [
        columns.map((column) => {
          return column.description;
        }),
        ...serviceOrders.map((serviceOrder) =>
          columns.map((column) => {
            const value = (serviceOrder as SummaryServiceOrder)[column.dataKey] || '';
            return column.renderValue
              ? column.renderValue(value, serviceOrder as SummaryServiceOrder)
              : value;
          }),
        ),
      ]
    );
  }, [columns, serviceOrders]);

  const dateRangeValue: [ValuePiece, ValuePiece] = React.useMemo(
    () => [new Date(beginDate), new Date(endDate)],
    [beginDate, endDate],
  );

  return (
    <Box
      sx={{
        flex: '0 0 auto',
      }}
    >
      <DateRangePicker
        onChange={(value: Value) => {
          if (Array.isArray(value)) {
            setDates(
              dayjs(value[0]).startOf('day').toISOString(),
              dayjs(value[1]).endOf('day').toISOString(),
            );
          }
        }}
        value={dateRangeValue}
        minDate={dayjs().subtract(3, 'months').toDate()}
        maxDate={dayjs().endOf('month').toDate()}
        label={t('_common:period')}
        minDetail="year"
      />
      <ButtonGroup variant="outlined" color="primary" sx={{ float: 'right' }}>
        <Button onClick={handlePrintClick} sx={{ borderColor: 'action.disabled' }}>
          <Print />
        </Button>
        <Button sx={{ borderColor: 'action.disabled' }}>
          <Box
            component={CSVLink}
            data={eventsCsvData || []}
            filename={`${t('_common:service_orders')} - ${dayjs().format('L LTS')}`}
            sx={{
              color: 'currentcolor',
              textDecoration: 'none',
              display: 'flex',
            }}
          >
            <CloudDownload />
          </Box>
        </Button>
      </ButtonGroup>
    </Box>
  );
}

export default ServiceOrdersActions;
